import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

const Book = () => {
  const { t } = useLanguage();
  const amazonUrl = "https://amzn.to/4sb4l4x";
  const bookCover = "https://media-manager-c.questera.ai/greta-media/6f319949afba45b9008649532004af25bb95492e2b2fb555f5bdcdad5193a9e70a9e6d92944cfc73f8b57561372ade93/images/aW1hZ2UvcG5n/053c33f4bca8666ccd85eaa70369a081.png";

  return (
    <div className="min-h-screen bg-gallery-bg">
      {/* Hero Section */}
      <section className="relative h-screen w-full flex items-center justify-center overflow-hidden bg-black">
        <div className="absolute inset-0 z-0">
          <img 
            src={bookCover} 
            alt="Olivia Grey Background"
            className="w-full h-full object-cover opacity-30 scale-110 blur-sm"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/60"></div>
        </div>
        
        <div className="relative z-10 text-center px-4">
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="text-gallery-accent uppercase tracking-[0.4em] text-sm mb-4"
          >
            {t('book.heroSubtitle')}
          </motion.p>
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="text-6xl md:text-8xl lg:text-9xl font-serif text-white mb-8 tracking-tighter"
          >
            {t('book.heroTitle')}
          </motion.h1>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.4 }}
          >
            <a 
              href={amazonUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-white text-black px-10 py-4 uppercase tracking-widest text-xs font-bold hover:bg-gallery-accent hover:text-white transition-all duration-500"
            >
              {t('book.buyAmazon')}
            </a>
          </motion.div>
        </div>
      </section>

      {/* Book Detail Section */}
      <section className="py-32 px-6 max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row items-center gap-16 lg:gap-24">
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="w-full lg:w-1/2"
          >
            <div className="relative aspect-[3/4] bg-gallery-surface shadow-2xl overflow-hidden group">
              <img 
                src={bookCover} 
                alt="Olivia Grey Book Cover"
                className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
              />
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="w-full lg:w-1/2 space-y-8"
          >
            <div className="space-y-2">
              <span className="inline-block px-3 py-1 bg-gallery-accent/10 text-gallery-accent text-[10px] uppercase tracking-widest rounded-full">
                {t('book.stats.genreValue')}
              </span>
              <h2 className="text-4xl md:text-5xl font-serif">{t('book.heroTitle')}</h2>
            </div>
            
            <div className="space-y-6">
              <h3 className="text-xs uppercase tracking-[0.2em] text-gallery-muted border-b border-gallery-border pb-2">
                {t('book.synopsisTitle')}
              </h3>
              <p className="text-lg text-gallery-text leading-relaxed font-light italic">
                "{t('book.synopsisText')}"
              </p>
            </div>

            <a 
              href={amazonUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block w-full text-center bg-gallery-accent text-white py-5 uppercase tracking-widest text-sm hover:bg-gallery-text transition-colors duration-500"
            >
              {t('book.buyAmazon')}
            </a>
          </motion.div>
        </div>
      </section>

      {/* About & Stats Section */}
      <section className="bg-white py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-20">
            <div className="space-y-8">
              <h2 className="text-3xl font-serif">{t('book.aboutTitle')}</h2>
              <div className="w-12 h-px bg-gallery-accent"></div>
              <p className="text-gallery-muted leading-relaxed text-lg font-light">
                {t('book.description')}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-x-8 gap-y-12">
              {[
                { label: t('book.stats.genre'), value: t('book.stats.genreValue') },
                { label: t('book.stats.year'), value: t('book.stats.yearValue') },
                { label: t('book.stats.inspiration'), value: t('book.stats.inspirationValue') },
                { label: t('book.stats.dedicated'), value: t('book.stats.dedicatedValue') }
              ].map((stat, i) => (
                <div key={i} className="space-y-2">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-gallery-muted">{stat.label}</p>
                  <p className="text-lg font-serif italic">{stat.value}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Book;